import { type FC, useState, useEffect, useRef } from "react";
import { LoaderCircle } from "lucide-react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

import { cn } from "@/shared/utils/core";

import findingDocsAnimation from "@/shared/assets/animations/finding_docs.lottie?url";

const MESSAGE_INTERVAL = 2500;
const DELAY_THRESHOLD_MS = 45_000;

const DELAYED_MESSAGES = [
  "Still working on it, hang tight",
  "Almost there, just a bit longer",
  "Processing is taking a moment",
  "Wrapping things up",
] as const;

interface DocumentProcessingOverlayProps {
  messages: readonly string[];
}

export const DocumentProcessingOverlay: FC<DocumentProcessingOverlayProps> = ({ messages }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isDelayed, setIsDelayed] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      fadeTimerRef.current = setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
        setVisible(true);
      }, 300);
    }, MESSAGE_INTERVAL);

    const delayTimer = setTimeout(() => setIsDelayed(true), DELAY_THRESHOLD_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(delayTimer);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [messages.length]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative h-[480px] w-[480px]">
        <DotLottieReact src={findingDocsAnimation} loop autoplay />
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[220px] bg-gradient-to-r from-stone-50 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[280px] bg-gradient-to-l from-stone-50 to-transparent" />
      </div>
      <p className="text-sm text-secondary">
        {isDelayed ? "Taking longer than expected..." : "This should only take a moment"}
      </p>
      <div
        className={cn(
          "flex items-center justify-center gap-3 transition-all duration-300",
          visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
        )}
      >
        <LoaderCircle className="h-6 w-6 shrink-0 animate-spin text-secondary" />
        <p className="text-xl">
          {isDelayed
            ? DELAYED_MESSAGES[messageIndex % DELAYED_MESSAGES.length]
            : messages[messageIndex]}
        </p>
      </div>
    </div>
  );
};
